import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DespachoInfoCallout,
  DespachoSectionCard,
} from "@/components/despacho/DespachoUi";
import {
  despachoFiscalInfoApi,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";
import { isDespachoReadOnly } from "@/modules/despacho/despachoPermissions";
import { useAuth } from "@/lib/auth";
import { useCapabilities } from "@/lib/useCapabilities";
import ObligationCatalogPicker from "@/pages/despacho/ObligationCatalogPicker";

const DEFAULT_REMINDER_OFFSETS = [-5, 0, 1];
const DEFAULT_ESCALATION_OFFSETS = [3, 5];

const EMPTY_NEW_FAMILY = {
  label: "",
  description: "",
  obligationIds: [],
  reminderOffsets: [...DEFAULT_REMINDER_OFFSETS],
  escalationOffsets: [...DEFAULT_ESCALATION_OFFSETS],
};

function emptyNewFamily() {
  return {
    ...EMPTY_NEW_FAMILY,
    obligationIds: [],
    reminderOffsets: [...DEFAULT_REMINDER_OFFSETS],
    escalationOffsets: [...DEFAULT_ESCALATION_OFFSETS],
  };
}

function normalizeOffsetList(raw, fallback) {
  if (!Array.isArray(raw) || !raw.length) return [...fallback];
  const nums = raw.map((n) => Number(n)).filter((n) => Number.isFinite(n));
  if (!nums.length) return [...fallback];
  return [...new Set(nums)].sort((a, b) => a - b);
}

function clampOffset(value, { min = -90, max = 90 } = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(min, Math.min(max, Math.trunc(n)));
}

function offsetHelpText(days) {
  if (days < 0) return `${Math.abs(days)} días antes del vencimiento`;
  if (days === 0) return "Día de vencimiento";
  return `${days} día${days === 1 ? "" : "s"} después del vencimiento`;
}

function ReminderScheduleEditor({ reminderOffsets, escalationOffsets, readOnly, onChange }) {
  const aux = normalizeOffsetList(reminderOffsets, DEFAULT_REMINDER_OFFSETS);
  const esc = normalizeOffsetList(escalationOffsets, DEFAULT_ESCALATION_OFFSETS).filter((d) => d > 0);

  const updateAux = (next) => onChange({ reminder_offsets: next, escalation_offsets: esc });
  const updateEsc = (next) => onChange({ reminder_offsets: aux, escalation_offsets: next });

  return (
    <div className="sm:col-span-2 space-y-4 rounded-sm border border-slate-200 bg-slate-50/60 p-3">
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs font-medium">Recordatorios al auxiliar</Label>
          {!readOnly ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-sm h-7 text-xs"
              onClick={() => updateAux([...aux, aux.length ? aux[aux.length - 1] + 1 : -5])}
            >
              <Plus className="h-3 w-3 mr-1" aria-hidden />
              Agregar
            </Button>
          ) : null}
        </div>
        <p className="text-[11px] text-slate-500 m-0 leading-snug">
          Días respecto al vencimiento (negativo = antes, 0 = día del vencimiento, positivo = después). Si la fecha
          calculada cae en fin de semana o inhábil, se ajusta al día hábil previo o siguiente.
        </p>
        <ul className="space-y-2 m-0 p-0 list-none">
          {aux.map((offset, idx) => (
            <li key={`aux-${idx}`} className="flex flex-wrap items-center gap-2">
              <input
                type="number"
                min={-90}
                max={90}
                className="flex h-8 w-20 rounded-sm border border-slate-200 px-2 text-sm bg-white"
                value={offset}
                disabled={readOnly}
                onChange={(e) => {
                  const next = [...aux];
                  next[idx] = clampOffset(e.target.value);
                  updateAux([...new Set(next)].sort((a, b) => a - b));
                }}
              />
              <span className="text-xs text-slate-600">{offsetHelpText(offset)}</span>
              {!readOnly && aux.length > 1 ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs text-rose-700"
                  onClick={() => updateAux(aux.filter((_, i) => i !== idx))}
                >
                  Quitar
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      </div>

      <div className="space-y-2 border-t border-slate-200 pt-3">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs font-medium">Escalamiento al supervisor (opcional)</Label>
          {!readOnly ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-sm h-7 text-xs"
              onClick={() => updateEsc([...esc, (esc[esc.length - 1] || 2) + 2])}
            >
              <Plus className="h-3 w-3 mr-1" aria-hidden />
              Agregar
            </Button>
          ) : null}
        </div>
        <p className="text-[11px] text-slate-500 m-0 leading-snug">
          Días <strong>después</strong> del vencimiento si la obligación sigue pendiente. Deje vacío para no escalar.
        </p>
        {esc.length === 0 ? (
          <p className="text-xs text-slate-500 m-0">Sin escalamiento configurado.</p>
        ) : (
          <ul className="space-y-2 m-0 p-0 list-none">
            {esc.map((offset, idx) => (
              <li key={`esc-${idx}`} className="flex flex-wrap items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={90}
                  className="flex h-8 w-20 rounded-sm border border-slate-200 px-2 text-sm bg-white"
                  value={offset}
                  disabled={readOnly}
                  onChange={(e) => {
                    const next = [...esc];
                    next[idx] = clampOffset(e.target.value, { min: 1, max: 90 });
                    updateEsc([...new Set(next)].sort((a, b) => a - b));
                  }}
                />
                <span className="text-xs text-slate-600">{offsetHelpText(offset)}</span>
                {!readOnly ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs text-rose-700"
                    onClick={() => updateEsc(esc.filter((_, i) => i !== idx))}
                  >
                    Quitar
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function AccordionSectionCard({ title, description, isOpen, onToggle, children, testId }) {
  return (
    <section
      className="rounded-sm border p-0 space-y-0 border-slate-200 bg-white"
      data-testid={testId}
    >
      <button
        type="button"
        className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        <span className="min-w-0">
          <span className="block text-sm font-bold text-[#0F2D5C] truncate">{title}</span>
          {description ? (
            <span className="block text-xs text-slate-500 mt-1 line-clamp-2">{description}</span>
          ) : null}
        </span>
        <ChevronDown
          className={`mt-0.5 h-4 w-4 shrink-0 text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      {isOpen ? (
        <div className="border-t border-slate-100 p-4 space-y-3">
          {children}
        </div>
      ) : null}
    </section>
  );
}

function GroupEditorTabs({
  group,
  catalog,
  readOnly,
  savingId,
  deletingId,
  onChangeGroup,
  onToggleObligation,
  onSave,
  onDelete,
}) {
  const obligationCount = (group.obligations || []).length;

  return (
    <Tabs defaultValue="datos" className="space-y-3">
      <TabsList className="rounded-sm h-8">
        <TabsTrigger value="datos" className="rounded-sm text-xs">
          Datos del grupo
        </TabsTrigger>
        <TabsTrigger value="obligaciones" className="rounded-sm text-xs">
          Seleccionar obligaciones ({obligationCount})
        </TabsTrigger>
      </TabsList>

      <TabsContent value="datos" className="mt-0 space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label className="text-xs">Nombre visible</Label>
            <input
              className="flex h-9 w-full rounded-sm border border-slate-200 px-3 text-sm bg-white"
              value={group.label || ""}
              disabled={readOnly}
              onChange={(e) => onChangeGroup(group.id, { label: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Descripción</Label>
            <input
              className="flex h-9 w-full rounded-sm border border-slate-200 px-3 text-sm bg-white"
              value={group.description || ""}
              disabled={readOnly}
              onChange={(e) => onChangeGroup(group.id, { description: e.target.value })}
            />
          </div>
          <ReminderScheduleEditor
            reminderOffsets={group.reminder_offsets}
            escalationOffsets={group.escalation_offsets}
            readOnly={readOnly}
            onChange={(patch) => onChangeGroup(group.id, patch)}
          />
        </div>
      </TabsContent>

      <TabsContent value="obligaciones" className="mt-0">
        {catalog.length ? (
          <ObligationCatalogPicker
            catalog={catalog}
            selectedIds={(group.obligations || []).map((o) => o.id)}
            readOnly={readOnly}
            onToggle={(oid, checked) => onToggleObligation(group.id, oid, checked)}
            testId={`group-obligations-${group.id}`}
          />
        ) : (
          <p className="text-sm text-slate-500 m-0">No se pudo cargar el catálogo de obligaciones.</p>
        )}
      </TabsContent>

      {!readOnly ? (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            className="rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248]"
            disabled={savingId === group.id || deletingId === group.id}
            onClick={() => onSave(group)}
          >
            {savingId === group.id ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
            Guardar grupo
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-sm text-rose-700 border-rose-200 hover:bg-rose-50"
            disabled={savingId === group.id || deletingId === group.id}
            onClick={() => onDelete(group)}
          >
            {deletingId === group.id ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
            {group.is_custom ? "Eliminar grupo" : "Quitar del listado"}
          </Button>
        </div>
      ) : null}
    </Tabs>
  );
}

export default function CalendarioReminderBundleGroupsPanel({
  testId = "calendario-reminder-bundle-groups",
}) {
  const { user } = useAuth();
  const { permissions, role, isPlatformDesigner, loading: capabilitiesLoading } = useCapabilities();
  const readOnly = isDespachoReadOnly({
    permissions,
    role,
    isPlatformDesigner,
    capabilitiesLoading,
    user,
  });
  const [groups, setGroups] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [savingId, setSavingId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [ensuringDefaults, setEnsuringDefaults] = useState(false);
  const [creatingFamily, setCreatingFamily] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newFamily, setNewFamily] = useState(() => emptyNewFamily());
  const [openGroupId, setOpenGroupId] = useState("");

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      const [rows, catalogRows] = await Promise.all([
        despachoFiscalInfoApi.listReminderBundleGroups(),
        despachoFiscalInfoApi.listCatalog(),
      ]);
      setGroups(rows);
      setCatalog(catalogRows);
      setLoadState("ready");
    } catch (err) {
      setGroups([]);
      setLoadState("error");
      toast.error(parseFiscalInfoError(err).message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const fiscalCatalog = useMemo(
    () => (catalog || []).filter((row) => String(row.authority || "") !== "Interna"),
    [catalog],
  );

  const ensureDefaults = async () => {
    if (readOnly) return;
    setEnsuringDefaults(true);
    try {
      const result = await despachoFiscalInfoApi.ensureReminderBundleGroupDefaults();
      const inserted = result.inserted ?? 0;
      toast.success(
        inserted > 0
          ? `Se instalaron ${inserted} grupo(s) predeterminado(s) (SAT, IMSS, REPSE…).`
          : "Los grupos predeterminados ya estaban disponibles.",
      );
      await load();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setEnsuringDefaults(false);
    }
  };

  const saveGroup = async (group) => {
    if (readOnly) return;
    setSavingId(group.id);
    try {
      await despachoFiscalInfoApi.updateReminderBundleGroup(group.id, {
        label: group.label,
        description: group.description,
        obligation_catalog_ids: (group.obligations || []).map((o) => o.id),
        reminder_offsets: normalizeOffsetList(group.reminder_offsets, DEFAULT_REMINDER_OFFSETS),
        escalation_offsets: normalizeOffsetList(group.escalation_offsets, DEFAULT_ESCALATION_OFFSETS).filter(
          (d) => d > 0,
        ),
        active: group.active !== false,
      });
      toast.success(`Grupo «${group.label}» actualizado.`);
      await load();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setSavingId("");
    }
  };

  const createFamily = async () => {
    if (readOnly || creatingFamily) return;
    const label = newFamily.label.trim();
    if (!label) {
      toast.error("Indique un nombre para el grupo.");
      return;
    }
    setCreatingFamily(true);
    try {
      const result = await despachoFiscalInfoApi.createReminderBundleGroup({
        label,
        description: newFamily.description.trim() || label,
        obligation_catalog_ids: newFamily.obligationIds,
        reminder_offsets: normalizeOffsetList(newFamily.reminderOffsets, DEFAULT_REMINDER_OFFSETS),
        escalation_offsets: normalizeOffsetList(newFamily.escalationOffsets, DEFAULT_ESCALATION_OFFSETS).filter(
          (d) => d > 0,
        ),
        active: true,
      });
      toast.success(`Grupo «${label}» creado.`);
      setNewFamily(emptyNewFamily());
      setShowNewForm(false);
      if (result?.item?.id) {
        setOpenGroupId(result.item.id);
      }
      await load();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setCreatingFamily(false);
    }
  };

  const deleteGroup = async (group) => {
    if (readOnly) return;
    const label = group.label || group.id;
    const msg = group.is_custom
      ? `¿Eliminar el grupo «${label}»? Esta acción no se puede deshacer.`
      : `¿Quitar «${label}» del listado de este despacho? Puede volver a instalarlo con «Instalar grupos predeterminados».`;
    if (!window.confirm(msg)) return;
    setDeletingId(group.id);
    try {
      const result = await despachoFiscalInfoApi.deleteReminderBundleGroup(group.id);
      if (result.mode === "removed") {
        toast.success(`Grupo «${label}» eliminado.`);
      } else {
        toast.success(`Grupo «${label}» quitado del listado.`);
      }
      await load();
      if (openGroupId === group.id) {
        setOpenGroupId("");
      }
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setDeletingId("");
    }
  };

  const patchGroup = (groupId, patch) => {
    setGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, ...patch } : g)));
  };

  const toggleObligation = (groupId, obligationId, checked) => {
    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g;
        const current = new Set((g.obligations || []).map((o) => o.id));
        if (checked) current.add(obligationId);
        else current.delete(obligationId);
        const obligations = [...current].map((id) => {
          const existing = (g.obligations || []).find((o) => o.id === id);
          if (existing) return existing;
          const cat = fiscalCatalog.find((o) => o.id === id);
          return {
            id,
            nombre_visible: cat?.nombre_visible || cat?.short_name || id,
          };
        });
        return { ...g, obligations };
      }),
    );
  };

  const toggleNewFamilyObligation = (obligationId, checked) => {
    setNewFamily((prev) => {
      const set = new Set(prev.obligationIds);
      if (checked) set.add(obligationId);
      else set.delete(obligationId);
      return { ...prev, obligationIds: [...set] };
    });
  };

  if (loadState === "loading") {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600 py-6">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Cargando grupos de obligaciones…
      </div>
    );
  }

  if (loadState === "error") {
    return (
      <DespachoSectionCard title="Grupos de obligaciones">
        <p className="text-sm text-slate-600 m-0 mb-2">No se pudieron cargar los grupos.</p>
        <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={load}>
          Reintentar
        </Button>
      </DespachoSectionCard>
    );
  }

  return (
    <div className="space-y-4" data-testid={testId}>
      <DespachoInfoCallout>
        Agrupe <strong>impuestos relacionados</strong> que vencen el <strong>mismo día del mes</strong>. En cada grupo
        defina <strong>cuántos recordatorios</strong> enviar al auxiliar y con qué <strong>diferencia de días</strong>
        respecto al vencimiento.
      </DespachoInfoCallout>

      {readOnly ? (
        <p className="text-xs text-amber-800 m-0 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2">
          Modo solo lectura: puede ver los grupos pero no editarlos.
        </p>
      ) : null}

      {!readOnly ? (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-sm h-8"
            onClick={() => setShowNewForm((v) => !v)}
          >
            <Plus className="h-3.5 w-3.5 mr-1" aria-hidden />
            Nuevo grupo de obligaciones
          </Button>
          {groups.length === 0 ? (
            <Button
              type="button"
              size="sm"
              className="rounded-sm h-8 bg-[#0F2D5C] hover:bg-[#0a2248]"
              onClick={ensureDefaults}
              disabled={ensuringDefaults}
            >
              {ensuringDefaults ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" aria-hidden /> : null}
              Instalar grupos predeterminados (SAT, IMSS, REPSE…)
            </Button>
          ) : null}
        </div>
      ) : null}

      {showNewForm && !readOnly ? (
        <DespachoSectionCard title="Nuevo grupo de obligaciones">
          <Tabs defaultValue="datos" className="space-y-3">
            <TabsList className="rounded-sm h-8">
              <TabsTrigger value="datos" className="rounded-sm text-xs">
                Datos del grupo
              </TabsTrigger>
              <TabsTrigger value="obligaciones" className="rounded-sm text-xs">
                Seleccionar obligaciones ({newFamily.obligationIds.length})
              </TabsTrigger>
            </TabsList>
            <TabsContent value="datos" className="mt-0 space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs">Nombre visible</Label>
                  <input
                    className="flex h-9 w-full rounded-sm border border-slate-200 px-3 text-sm bg-white"
                    value={newFamily.label}
                    placeholder="Ej. Estatales mensuales"
                    onChange={(e) => setNewFamily((p) => ({ ...p, label: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Descripción</Label>
                  <input
                    className="flex h-9 w-full rounded-sm border border-slate-200 px-3 text-sm bg-white"
                    value={newFamily.description}
                    onChange={(e) => setNewFamily((p) => ({ ...p, description: e.target.value }))}
                  />
                </div>
                <ReminderScheduleEditor
                  reminderOffsets={newFamily.reminderOffsets}
                  escalationOffsets={newFamily.escalationOffsets}
                  readOnly={false}
                  onChange={(patch) =>
                    setNewFamily((p) => ({
                      ...p,
                      reminderOffsets: patch.reminder_offsets ?? p.reminderOffsets,
                      escalationOffsets: patch.escalation_offsets ?? p.escalationOffsets,
                    }))
                  }
                />
              </div>
            </TabsContent>
            <TabsContent value="obligaciones" className="mt-0">
              <ObligationCatalogPicker
                catalog={fiscalCatalog}
                selectedIds={newFamily.obligationIds}
                onToggle={toggleNewFamilyObligation}
                testId="new-group-obligations"
              />
            </TabsContent>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                className="rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248]"
                disabled={creatingFamily}
                onClick={createFamily}
              >
                {creatingFamily ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                Crear grupo
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="rounded-sm"
                onClick={() => {
                  setShowNewForm(false);
                  setNewFamily(emptyNewFamily());
                }}
              >
                Cancelar
              </Button>
            </div>
          </Tabs>
        </DespachoSectionCard>
      ) : null}

      {groups.length === 0 ? (
        <DespachoSectionCard title="Sin grupos configurados">
          <p className="text-sm text-slate-600 m-0 mb-3">
            Instale los grupos predeterminados o cree uno nuevo y seleccione obligaciones en la pestaña correspondiente.
          </p>
          {!readOnly ? (
            <Button
              type="button"
              size="sm"
              className="rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248]"
              onClick={ensureDefaults}
              disabled={ensuringDefaults}
            >
              Instalar grupos predeterminados
            </Button>
          ) : null}
        </DespachoSectionCard>
      ) : null}

      {groups.map((group) => (
        <AccordionSectionCard
          key={group.id}
          title={group.label || group.id}
          description={group.description || group.id}
          isOpen={openGroupId === group.id}
          onToggle={() => setOpenGroupId((current) => (current === group.id ? "" : group.id))}
        >
          <GroupEditorTabs
            group={group}
            catalog={fiscalCatalog}
            readOnly={readOnly}
            savingId={savingId}
            deletingId={deletingId}
            onChangeGroup={patchGroup}
            onToggleObligation={toggleObligation}
            onSave={saveGroup}
            onDelete={deleteGroup}
          />
        </AccordionSectionCard>
      ))}
    </div>
  );
}
